import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Trans } from 'react-i18next';
import * as yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import SentryManager from '@widgets/sentry-manager';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import CancelIcon from '@mui/icons-material/Cancel';
import CircularProgress from '@mui/material/CircularProgress';
import { regexAnyCharAtLeastOneNumberAndLetter } from '@constants/regex';
import { BRAND_TEAL } from '@constants/colors';
import { AuthLayout } from '@features/AuthLayout';
import { useTranslation, useYupValidationResolver, useQueryParams } from '@hooks';
import { authService } from '@services';
import { authActions } from '@actions';
import PostHogManager from '@widgets/posthog-manager';
import { ProductEvent } from '@constants/product-events';
import GTManager, { buildUserParams } from '@widgets/gtm';
import { setUserInLocalStorage } from '@helpers/auth';
import { handleApiError } from '@helpers/api';
import { getPartnerCode, rememberPartnerCode } from '@helpers/partnerLink';
import { InternalLink, ExternalLink } from '@components/Link';
import Button from '@components/Button';
import { AuthProgress } from '@components/AuthProgress';
import { Input } from '@components/Input';
import { Typography } from '@components/Typography';
import GoogleButtonAuth from '@features/GoogleButtonAuth';
import { RoutePath, ExternalRoutes } from '@constants/routes';
import { SIGNUP_LOCALES } from '@constants/locale';

const Form = styled(Paper)({
    marginBottom: '24px',
    padding: '24px',
    width: 400,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    '@media (max-width: 600px)': {
        padding: '16px',
        width: '100%',
    },
});

const Overlay = styled('div')({
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(255,255,255,0.6)',
    zIndex: 2,
});

const TnC = styled(Typography)({
    width: 352,
    textAlign: 'center',
    '@media (max-width: 600px)': {
        width: '100%',
    },
});

const ProgressGoogleWrapper = styled('div')({
    position: 'absolute',
    zIndex: 3,
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    bottom: 52,
    top: 0,
    background: 'rgba(255, 255, 255, .7)',
    '& .MuiCircularProgress-root': {
        color: BRAND_TEAL,
        width: '58px !important',
        height: '58px !important',
        display: 'block',
        margin: '0 auto 24px',
    },
});

const SignupForm = () => {
    const { t } = useTranslation();
    const query = useQueryParams();
    // Check query parameter first (explicit user choice)
    const queryLng = query.get('lng') || '';
    // If no query param, check localStorage but treat auto-saved 'GB' as "not explicitly set"
    const storedLng = !queryLng ? localStorage.getItem('i18nextLng') || '' : '';
    const lng = queryLng || (storedLng && storedLng !== 'GB' ? storedLng : '');
    const partnerCode = query.get('partner');

    useEffect(() => rememberPartnerCode(partnerCode), [partnerCode]);

    const [googleAuthLoading, setGoogleAuthLoading] = useState(false);
    const [isEmailFocused, setEmailFocused] = useState(false);

    const dispatch = useDispatch();

    const SignupFormSchema = yup.object().shape({
        email: yup.string().required('validation.required').email('validation.wrongEmailFormat'),
        password: yup
            .string()
            .min(8, 'validation.minPasswordLength')
            .matches(regexAnyCharAtLeastOneNumberAndLetter, 'validation.wrongPasswordFormat')
            .required('validation.required'),
    });

    const resolver = useYupValidationResolver(SignupFormSchema);

    const {
        getValues,
        handleSubmit,
        setValue,
        control,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: 'all',
        resolver: resolver,
        defaultValues: {
            email: '',
            password: '',
        },
    });

    const onSubmit = async () => {
        const values = getValues();
        try {
            const payload = {
                language: lng || 'US',
                password: values.password,
                email: values.email,
                setInternationalLocale: !SIGNUP_LOCALES.includes(lng.toUpperCase()),
                // eslint-disable-next-line
                hubspotToken: document.cookie.replace(/(?:(?:^|.*;\s*)hubspotutk\s*\=\s*([^;]*).*$)|^.*$/, '$1'),
                registrationPageUrl: window.location.href,
                partnerCode: getPartnerCode(),
            };
            const user = await authService.signup(payload);
            setUserInLocalStorage(user);
            GTManager.update(user);
            GTManager.setRegistrationType('email');
            GTManager.push('user-registered', {
                ...buildUserParams(user),
                registration_type: 'email',
            });
            PostHogManager.identify(user);
            if (user.isNewUser) {
                PostHogManager.capture(ProductEvent.UserRegistered, { registration_type: 'email' });
            }
            SentryManager.setUser(user);
            dispatch(authActions.setUser(user));
        } catch (error) {
            handleApiError(error, '', { email: values.email });
        }
    };

    return (
        <AuthLayout>
            <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {googleAuthLoading && (
                    <ProgressGoogleWrapper>
                        <CircularProgress />
                    </ProgressGoogleWrapper>
                )}
                {isSubmitting && <AuthProgress />}
                <Form elevation={24}>
                    {isSubmitting && <Overlay />}
                    <Typography variant="h5" sx={{ marginBottom: '8px', color: 'rgba(0, 0, 0, .87)' }}>
                        {t('SignupPage.title')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                        {t('SignupPage.description')}
                    </Typography>
                    <GoogleButtonAuth
                        language={lng || 'US'}
                        text={t('SignupPage.signupWithGoogle')}
                        path="signup"
                        action="signup"
                        onClickCallback={setGoogleAuthLoading}
                    />
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
                        {t('SignupPage.signupWithGoogleCaption')}
                    </Typography>
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginBottom: '16px' }}>
                        <span style={{ borderTop: '1px solid rgba(0, 0, 0, 0.12)', width: '100%' }} />
                        <Typography variant="caption" color="textSecondary" style={{ padding: '0 16px' }}>
                            {t('SignupPage.or')}
                        </Typography>
                        <span style={{ borderTop: '1px solid rgba(0, 0, 0, 0.12)', width: '100%' }} />
                    </div>
                    <Controller
                        control={control}
                        name="email"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field, type: 'email' }}
                                onAddonClick={() => setValue('email', '')}
                                addonComponent={isEmailFocused ? <CancelIcon style={{ fontSize: 16 }} /> : null}
                                label={t('common.emailAddress')}
                                fullWidth
                                sx={{ marginBottom: '16px' }}
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                                onMouseEnter={() => setEmailFocused(true)}
                                onMouseLeave={() => setEmailFocused(false)}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="password"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field }}
                                type="password"
                                label={t('common.password')}
                                sx={{ marginBottom: '16px' }}
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                            />
                        )}
                    />
                    <Button
                        id="pl-button-signup-submit"
                        onClick={handleSubmit(onSubmit)}
                        variant="contained"
                        fullWidth
                        sx={{ marginBottom: '16px' }}
                        disabled={isSubmitting || !isValid || !isDirty}
                    >
                        {t('buttons.signupButton')}
                    </Button>
                    <TnC variant="caption">
                        <Trans t={t} i18nKey="SignupPage.alreadyRegistered">
                            <InternalLink to={RoutePath.Login} variant="caption" align="center" underline="hover">
                                {{ link: t('SignupPage.logIn') }}
                            </InternalLink>
                        </Trans>
                    </TnC>
                </Form>
                <TnC variant="caption">
                    <Trans t={t} i18nKey="SignupPage.termsConditions">
                        <ExternalLink href={ExternalRoutes.TermsLink} variant="caption" underline="hover">
                            {{ terms: t('SignupPage.terms') }}
                        </ExternalLink>
                        <ExternalLink href={ExternalRoutes.PrivacyLink} variant="caption" underline="hover">
                            {{ privacy: t('SignupPage.privacy') }}
                        </ExternalLink>
                        <ExternalLink href={ExternalRoutes.DPALink} variant="caption" underline="hover">
                            {{ dpa: t('SignupPage.dpa') }}
                        </ExternalLink>
                    </Trans>
                </TnC>
            </div>
        </AuthLayout>
    );
};

export default SignupForm;
